// src/components/workflows/views/ScheduleControl.jsx - Schedule configuration for automation dashboard
import React from 'react';
import { Clock } from 'lucide-react';
import { Switch } from '../../ui/switch.tsx';
import { Label } from '../../ui/label.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select.tsx';
import { Input } from '../../ui/input.tsx';

/**
 * ScheduleControl - Allows editing automation schedule
 *
 * Props:
 *   schedule: { enabled, type, time, timezone, interval_value, interval_unit, day_of_week }
 *   onUpdate: (updates) => void
 */
const ScheduleControl = ({ schedule = {}, onUpdate }) => {
  const {
    enabled = false,
    type = 'daily',
    time = '08:00',
    timezone = 'America/Chicago',
    interval_value = 4,
    interval_unit = 'hours',
    day_of_week = 1
  } = schedule;

  const handleUpdate = (updates) => {
    onUpdate?.({ ...schedule, ...updates });
  };

  const TIMEZONES = [
    { value: 'America/New_York', label: 'Eastern (ET)' },
    { value: 'America/Chicago', label: 'Central (CT)' },
    { value: 'America/Denver', label: 'Mountain (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
    { value: 'UTC', label: 'UTC' },
  ];

  const inputClassName = "bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5";

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
          <Clock className="w-5 h-5" />
          Schedule
        </h3>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
          Configure when this automation runs automatically.
        </p>
      </div>

      <div className="space-y-4">
        {/* Schedule Type */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Frequency</Label>
          <Select value={type} onValueChange={(value) => handleUpdate({ type: value })}>
            <SelectTrigger className={inputClassName}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="interval">Interval</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Time (for daily/weekly) */}
        {(type === 'daily' || type === 'weekly') && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">Time</Label>
            <Input
              type="time"
              value={time}
              onChange={(e) => handleUpdate({ time: e.target.value })}
              className={inputClassName}
            />
          </div>
        )}

        {/* Day of Week (for weekly) */}
        {type === 'weekly' && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">Day</Label>
            <Select
              value={String(day_of_week)}
              onValueChange={(value) => handleUpdate({ day_of_week: parseInt(value) })}
            >
              <SelectTrigger className={inputClassName}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, i) => (
                  <SelectItem key={i} value={String(i)}>{day}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Interval config */}
        {type === 'interval' && (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Every</Label>
              <Input
                type="number"
                min="1"
                value={interval_value}
                onChange={(e) => handleUpdate({ interval_value: parseInt(e.target.value) || 1 })}
                className={inputClassName}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Unit</Label>
              <Select value={interval_unit} onValueChange={(value) => handleUpdate({ interval_unit: value })}>
                <SelectTrigger className={inputClassName}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="minutes">Minutes</SelectItem>
                  <SelectItem value="hours">Hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Timezone */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Timezone</Label>
          <Select value={timezone} onValueChange={(value) => handleUpdate({ timezone: value })}>
            <SelectTrigger className={inputClassName}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONES.map(tz => (
                <SelectItem key={tz.value} value={tz.value}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Enabled Toggle */}
        <div className="flex items-center justify-between p-4 border border-zinc-700/50 rounded-lg bg-zinc-900 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)]">
          <div>
            <Label className="font-medium text-sm">Schedule Enabled</Label>
            <p className="text-xs text-muted-foreground mt-1">Run automatically on schedule</p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={(checked) => handleUpdate({ enabled: checked })}
          />
        </div>
      </div>
    </div>
  );
};

export default ScheduleControl;
